import React, { useState } from 'react'
import {
    Box,
    Container,
    Flex,
    SimpleGrid,
    Button,
    Card, CardHeader, CardBody,
    Text,
    VStack, HStack,
    Badge,
    Divider,
    Icon,
    Heading,
    Drawer,
    DrawerBody,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    useDisclosure,
    useBreakpointValue,
    Center,
    IconButton,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    Input,
    InputGroup,
    InputLeftElement,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalCloseButton
  } from '@chakra-ui/react';
import { CalendarIcon, TimeIcon, CheckIcon, AddIcon, ViewIcon, HamburgerIcon, StarIcon, ArrowUpIcon, SearchIcon } from '@chakra-ui/icons';
import { ModernSidebar, GoalCard, UserMenu, LoadingButton, MobileBottomNav, useMobileBottomPadding, UserProfileModal } from './SharedComponents';

const Dashboard = ({ state, onRouteChange, getOverview, getUserStats, onSearch, onContinueChapter, onRandomChapter, onContinueJourney, onDeleteJourney, toggleSidebar, setLoading }) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { isOpen: isSearchOpen, onOpen: onSearchOpen, onClose: onSearchClose } = useDisclosure()
  const { isOpen: isProfileOpen, onOpen: onProfileOpen, onClose: onProfileClose } = useDisclosure()
  const isMobile = useBreakpointValue({ base: true, lg: false })
  const [searchQuery, setSearchQuery] = useState('')
  const bottomPadding = useMobileBottomPadding()
  
  React.useEffect(() => {
    // Always refresh the overview when Dashboard loads (without global loading)
    getOverview(false)
    // Get user stats (without global loading to avoid loading overload)
    getUserStats && getUserStats(false)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSearch = (e) => {
    const query = e.target.value
    setSearchQuery(query)
    if (onSearch) {
      onSearch(query, 'all', false) // false = no global loading for real-time search
    }
  }

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Mobile Navigation */}
      {isMobile && (
        <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
          <DrawerOverlay />
          <DrawerContent bg="white">
            <DrawerCloseButton color="gray.700" />
            <DrawerBody>
              <ModernSidebar
                state={state}
                onRouteChange={onRouteChange}
                getOverview={getOverview}
                onRandomChapter={onRandomChapter}
                onClose={onClose}
                toggleSidebar={undefined}
              />
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      )}
      
      <Flex>
        {/* Desktop Sidebar */}
        {!isMobile && !state.sidebarCollapsed && (
          <Box 
            w="280px" 
            bg="white" 
            minH="100vh" 
            position="fixed" 
            left={0} 
            top={0}
            zIndex={10}
            transition="all 0.3s ease"
            borderRight="1px"
            borderColor="gray.200"
            boxShadow="sm"
          >
            <ModernSidebar
              state={state}
              onRouteChange={onRouteChange}
              getOverview={getOverview} 
              onRandomChapter={onRandomChapter}
              toggleSidebar={toggleSidebar}
            />
          </Box>
        )}
        
        {/* Main Content */}
        <Box flex={1} ml={isMobile ? 0 : state.sidebarCollapsed ? 0 : "280px"} transition="margin-left 0.3s ease">
          {/* Top Navigation */}
          <Box bg="white" borderBottom="1px" borderColor="gray.200" px={{ base: 4, md: 6 }} py={{ base: 3, md: 3 }}>
            <Flex align="center" justify="space-between">
              <HStack spacing={3}>
                {isMobile && (
                  <IconButton
                    icon={<HamburgerIcon />}
                    onClick={onOpen}
                    variant="ghost"
                    colorScheme="brand"
                    size="sm"
                  />
                )}
                {!isMobile && state.sidebarCollapsed && (
                  <IconButton
                    icon={<HamburgerIcon />}
                    onClick={toggleSidebar}
                    variant="ghost"
                    color="brand.600"
                    size="sm"
                    _hover={{
                      color: 'brand.700',
                      bg: 'brand.50'
                    }}
                  />
                )}
                {!isMobile && (
                  <VStack align="start" spacing={0}>
                    <Heading size="lg" color="gray.800">
                      Your Practice
                    </Heading>
                    <Text fontSize="sm" color="gray.600">
                      {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                    </Text>
                  </VStack>
                )}
              </HStack>

              <HStack spacing={4}>
                {!isMobile && (
                  <>
                    <InputGroup maxW="250px">
                      <InputLeftElement pointerEvents='none'>
                        <SearchIcon color='gray.400' />
                      </InputLeftElement>
                      <Input
                        placeholder="Search goals..."
                        value={searchQuery}
                        onChange={handleSearch}
                        bg="white"
                        borderColor="gray.300"
                        size="sm"
                        _hover={{ borderColor: 'brand.300' }}
                        _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                      />
                    </InputGroup>
                    <Button
                      leftIcon={<AddIcon />}
                      variant="outline"
                      colorScheme="purple"
                      size="sm"
                      onClick={() => onRouteChange('createGoal')}
                    >
                      New Goal
                    </Button>
                  </>
                )}
                <UserMenu onRouteChange={onRouteChange} username={state.username} userStats={state.userStats} />
              </HStack>
            </Flex>
          </Box>
          
          {/* Dashboard Content */}
          <Container maxW="7xl" py={{ base: 4, md: 8 }} px={{ base: 4, md: 6 }} pb={bottomPadding}>
            {/* Search Results */}
            {state.searchResults && searchQuery && (
              <SearchResults 
                results={state.searchResults}
                query={state.searchQuery}
                onContinueJourney={onContinueJourney}
                onContinueChapter={onContinueChapter}
                onRouteChange={onRouteChange}
              />
            )}
            
            {/* Normal Dashboard Content */}
            {(!state.searchResults || !searchQuery) && (
              <>
                <TodaysPracticeSection
                  state={state}
                  onContinueChapter={onContinueChapter}
                  onRandomChapter={onRandomChapter}
                  onRouteChange={onRouteChange}
                  setLoading={setLoading}
                />
                
                <Box mt={12}>
                  <ProgressOverview state={state} />
                </Box>
                
                <Box mt={12}>
                  <GoalsList 
                    state={state}
                    onContinueJourney={onContinueJourney}
                    onDeleteJourney={onDeleteJourney}
                    onRouteChange={onRouteChange}
                    setLoading={setLoading}
                  />
                </Box>
              </>
            )}
          </Container>
        </Box>
      </Flex>

      {/* Mobile Search Modal */}
      <Modal isOpen={isSearchOpen} onClose={onSearchClose} size="full">
        <ModalOverlay />
        <ModalContent bg="gray.50" p={0}>
          <ModalCloseButton zIndex={3} />
          <Box p={6}>
            <VStack spacing={4} align="stretch">
              <Heading size="md" color="gray.800" mb={2}>Search</Heading>
              <InputGroup>
                <InputLeftElement pointerEvents='none'>
                  <SearchIcon color='gray.400' />
                </InputLeftElement>
                <Input
                  placeholder="Search goals & exercises..."
                  value={searchQuery}
                  onChange={handleSearch}
                  bg="white"
                  size="lg"
                  autoFocus
                />
              </InputGroup>
              
              {/* Search Results */}
              {state.searchResults && (
                <SearchResults 
                  results={state.searchResults}
                  query={state.searchQuery}
                  onContinueJourney={(id) => {
                    onContinueJourney(id)
                    onSearchClose()
                  }}
                  onContinueChapter={(id) => {
                    onContinueChapter(id)
                    onSearchClose()
                  }}
                  onRouteChange={(route, params) => {
                    onRouteChange(route, params)
                    onSearchClose()
                  }}
                />
              )}
            </VStack>
          </Box>
        </ModalContent>
      </Modal>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav
        currentRoute="dashboard"
        onRouteChange={onRouteChange}
        onRandomChapter={onRandomChapter}
        onSearchOpen={onSearchOpen}
        onProfileOpen={onProfileOpen}
      />

      {/* Profile Modal for Mobile */}
      <UserProfileModal
        isOpen={isProfileOpen}
        onClose={onProfileClose}
        username={state.username}
        userStats={state.userStats}
      />
    </Box>
  )
}

const TodaysPracticeSection = ({ state, onContinueChapter, onRandomChapter, onRouteChange, setLoading }) => {
  const overview = state.overview
  const todaysPractice = getTodaysPractice(overview)
  const [isStartingPractice, setIsStartingPractice] = useState(false)

  // Check if user has no goals at all (new user)
  const hasNoGoals = !overview.exists || !overview.items || overview.items.length === 0

  if (!todaysPractice) {
    return (
      <Card bg="white" shadow="xl" borderTop="4px" borderColor="brand.500">
        <CardBody p={{ base: 5, md: 8 }}>
          <Center>
            <VStack spacing={{ base: 4, md: 6 }}>
              <Box textAlign="center">
                <Icon as={CalendarIcon} boxSize={{ base: 10, md: 12 }} color="gray.400" mb={{ base: 3, md: 4 }} />
                <Heading size={{ base: 'sm', md: 'lg' }} color="gray.600" mb={2}>
                  {hasNoGoals ? 'Welcome to Grow' : 'Ready for Today\'s Practice?'}
                </Heading>
                <Text color="gray.500" fontSize={{ base: 'sm', md: 'lg' }} maxW="400px" px={2}>
                  {hasNoGoals
                    ? 'Watch the tutorial, or create a new goal to begin your journey.'
                    : 'Start with a random exercise from your goals, or create a new goal.'
                  }
                </Text>
              </Box>
              <VStack spacing={3} w="full" maxW="300px">
                {hasNoGoals ? (
                  <>
                    <Button
                      leftIcon={<ViewIcon />}
                      colorScheme="purple"
                      size={{ base: 'md', md: 'lg' }}
                      onClick={() => onRouteChange('tutorial')}
                      w="full"
                    >
                      Tutorial
                    </Button>
                    <Button
                      leftIcon={<AddIcon />}
                      variant="outline"
                      colorScheme="purple"
                      size={{ base: 'md', md: 'lg' }}
                      onClick={() => onRouteChange('createGoal')}
                      w="full"
                    >
                      Create New Goal
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      leftIcon={<AddIcon />}
                      colorScheme="purple"
                      size={{ base: 'md', md: 'lg' }}
                      onClick={() => onRouteChange('createGoal')}
                      w="full"
                    >
                      Create New Goal
                    </Button>
                    <Button
                      leftIcon={<ViewIcon />}
                      colorScheme="purple"
                      variant="outline"
                      size={{ base: 'md', md: 'lg' }}
                      onClick={() => onRouteChange('tutorial')}
                      w="full"
                    >
                      Tutorial
                    </Button>
                  </>
                )}
              </VStack>
            </VStack>
          </Center>
        </CardBody>
      </Card>
    )
  }

  const handleStartPractice = async () => {
    setIsStartingPractice(true)
    try {
      await onContinueChapter(todaysPractice.id)
    } finally {
      setIsStartingPractice(false)
    }
  }

  return (
    <Card bg="white" shadow="xl" borderTop="4px" borderColor="brand.500">
      <CardHeader bg="brand.50" py={{ base: 4, md: 6 }} px={{ base: 4, md: 6 }}>
        <VStack align="stretch" spacing={{ base: 2, md: 0 }}>
          <HStack justify="space-between" align="center" wrap="wrap" gap={2}>
            <HStack spacing={{ base: 2, md: 3 }}>
              <Icon as={StarIcon} color="brand.500" boxSize={{ base: 5, md: 6 }} />
              <VStack align="start" spacing={0}>
                <Heading size={{ base: 'sm', md: 'lg' }} color="brand.700">Today's Practice</Heading>
                <Text color="brand.600" fontSize={{ base: 'xs', md: 'sm' }} display={{ base: 'none', sm: 'block' }}>Your daily micro-exercise</Text>
              </VStack>
            </HStack>
            <Badge colorScheme="brand" variant="outline" px={{ base: 2, md: 3 }} py={1} borderRadius="full" fontSize={{ base: 'xs', md: 'sm' }}>
              <HStack spacing={1}>
                <TimeIcon boxSize={3} />
                <Text>{todaysPractice.time || '10 mins'}</Text>
              </HStack>
            </Badge>
          </HStack>
        </VStack>
      </CardHeader>

      <CardBody p={{ base: 4, md: 8 }}>
        <VStack spacing={{ base: 4, md: 6 }} align="stretch">
          <Box>
            <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.500" mb={{ base: 1, md: 2 }}>Exercise</Text>
            <Heading size={{ base: 'sm', md: 'md' }} color="gray.800" mb={{ base: 2, md: 3 }}>{todaysPractice.name}</Heading>
            <Text color="gray.600" lineHeight="tall" noOfLines={3} fontSize={{ base: 'sm', md: 'md' }}>
              {todaysPractice.description}
            </Text>
          </Box>

          <Divider />

          <VStack spacing={{ base: 3, md: 4 }} align="stretch">
            <HStack justify="space-between" align="center">
              <VStack align="start" spacing={0}>
                <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.500">From Goal</Text>
                <Text fontWeight="medium" color="gray.700" fontSize={{ base: 'sm', md: 'md' }} noOfLines={1}>
                  {todaysPractice.goalName || 'Personal Growth'}
                </Text>
              </VStack>
            </HStack>

            <LoadingButton
              size={{ base: 'md', md: 'lg' }}
              colorScheme="brand"
              leftIcon={<CheckIcon />}
              onClick={handleStartPractice}
              isLoading={isStartingPractice}
              loadingText="Starting..."
              _hover={{
                transform: 'translateY(-2px)',
                boxShadow: 'lg'
              }}
              w="full"
            >
              Start Practice
            </LoadingButton>
          </VStack>
        </VStack>
      </CardBody>
    </Card>
  )
}

const SearchResults = ({ results, query, onContinueJourney, onContinueChapter, onRouteChange }) => {
  const hasResults = results && (results.goals.length > 0 || results.exercises.length > 0)

  return (
    <Box>
      <Heading size="lg" color="gray.800" mb={6}>
        Search Results for "{query}"
      </Heading>
      
      {!hasResults && (
        <Card bg="white" shadow="md" p={8}>
          <Center>
            <VStack spacing={4}>
              <Icon as={SearchIcon} boxSize={12} color="gray.400" />
              <Text fontSize="lg" color="gray.600">No results found for "{query}"</Text>
              <Text color="gray.500">Try searching with different keywords</Text>
            </VStack>
          </Center>
        </Card>
      )}

      {/* Goals Results */}
      {results.goals.length > 0 && (
        <Box mb={8}>
          <Heading size="md" color="gray.700" mb={4}>Goals ({results.goals.length})</Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            {results.goals.map((goal) => (
              <Card key={goal.id} bg="white" shadow="md" _hover={{ shadow: 'lg' }} cursor="pointer"
                    onClick={() => onContinueJourney(goal.id)}>
                <CardBody p={6}>
                  <VStack align="start" spacing={3}>
                    <Heading size="sm" color="brand.700">{goal.name}</Heading>
                    <Text fontSize="sm" color="gray.600" noOfLines={2}>
                      {JSON.parse(goal.goal_details || '{}').goal || 'No description available'}
                    </Text>
                    <HStack>
                      <Badge colorScheme="brand">{goal.exercises_count || 0} exercises</Badge>
                      <Text fontSize="xs" color="gray.500">{goal.datetime}</Text>
                    </HStack>
                  </VStack>
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>
        </Box>
      )}

      {/* Exercises Results */}
      {results.exercises.length > 0 && (
        <Box>
          <Heading size="md" color="gray.700" mb={4}>Exercises ({results.exercises.length})</Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            {results.exercises.map((exercise) => (
              <Card key={exercise.id} bg="white" shadow="md" _hover={{ shadow: 'lg' }} cursor="pointer"
                    onClick={() => onContinueChapter(exercise.id)}>
                <CardBody p={6}>
                  <VStack align="start" spacing={3}>
                    <HStack justify="space-between" w="full">
                      <Heading size="sm" color="purple.700">{exercise.name}</Heading>
                      {exercise.completed && <CheckIcon color="green.500" />}
                    </HStack>
                    <Text fontSize="sm" color="gray.600" noOfLines={2}>
                      {exercise.description}
                    </Text>
                    <HStack>
                      <Badge colorScheme="purple">{exercise.goal_name}</Badge>
                      <Text fontSize="xs" color="gray.500">Part {exercise.part || 1}</Text>
                    </HStack>
                  </VStack>
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>
        </Box>
      )}
    </Box>
  )
}

const ProgressOverview = ({ state }) => {
  const overview = state.overview
  const userStats = state.userStats
  const stats = calculateStats(overview, userStats)

  return (
    <Box>
      <Heading size={{ base: 'md', md: 'lg' }} color="gray.800" mb={{ base: 4, md: 6 }}>Your Progress</Heading>
      <SimpleGrid columns={{ base: 3, md: 3 }} spacing={{ base: 2, md: 6 }}>
        <Card bg="white" shadow="md">
          <CardBody p={{ base: 3, md: 6 }}>
            <Stat>
              <StatLabel color="gray.600" fontSize={{ base: 'xs', md: 'sm' }}>Goals</StatLabel>
              <StatNumber color="brand.600" fontSize={{ base: 'xl', md: '3xl' }}>{stats.activeGoals}</StatNumber>
              <StatHelpText color="gray.500" fontSize={{ base: 'xs', md: 'sm' }} display={{ base: 'none', md: 'block' }}>
                <Icon as={ArrowUpIcon} mr={1} />
                Active
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card bg="white" shadow="md">
          <CardBody p={{ base: 3, md: 6 }}>
            <Stat>
              <StatLabel color="gray.600" fontSize={{ base: 'xs', md: 'sm' }}>Week</StatLabel>
              <StatNumber color="green.600" fontSize={{ base: 'xl', md: '3xl' }}>{stats.completedThisWeek}</StatNumber>
              <StatHelpText color="gray.500" fontSize={{ base: 'xs', md: 'sm' }} display={{ base: 'none', md: 'block' }}>
                Completed
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card bg="white" shadow="md">
          <CardBody p={{ base: 3, md: 6 }}>
            <Stat>
              <StatLabel color="gray.600" fontSize={{ base: 'xs', md: 'sm' }}>Streak</StatLabel>
              <StatNumber color="purple.600" fontSize={{ base: 'xl', md: '3xl' }}>{stats.streak}</StatNumber>
              <StatHelpText color="gray.500" fontSize={{ base: 'xs', md: 'sm' }} display={{ base: 'none', md: 'block' }}>
                <Icon as={CalendarIcon} mr={1} />
                Days
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>
    </Box>
  )
}

const GoalsList = ({ state, onContinueJourney, onDeleteJourney, onRouteChange, setLoading }) => {
  const overview = state.overview
  
  if (!overview.exists || !overview.items || overview.items.length === 0) {
    return null
  }

  return (
    <Box>
      <HStack justify="space-between" align="center" mb={6}>
        <Heading size="lg" color="gray.800">Your Goals</Heading>
        <Button
          variant="ghost"
          colorScheme="brand"
          rightIcon={<ViewIcon />}
          onClick={() => onRouteChange('allGoalsView')}
        >
          View All
        </Button>
      </HStack>
      
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
        {overview.items.slice(0, 4).map((goal) => (
          <GoalCard 
            key={goal.id}
            goal={goal}
            onContinueJourney={onContinueJourney}
            setLoading={setLoading}
            showDeleteButton={true}
            onDeleteJourney={onDeleteJourney}
          />
        ))}
      </SimpleGrid>
    </Box>
  )
}

// Using shared GoalCard component

// Using shared ModernSidebar component

// Using shared UserMenu component

// Helper functions
const getTodaysPractice = (overview) => {
  if (!overview.exists || !overview.items) return null
  
  // Find next incomplete exercise from all goals
  for (const goal of overview.items) {
    if (goal.exercises && Array.isArray(goal.exercises)) {
      const nextExercise = goal.exercises.find(ex => ex && ex.completed !== true)
      if (nextExercise) {
        return {
          ...nextExercise,
          goalName: goal.name
        }
      }
    }
  }
  return null
}

const calculateStats = (overview, userStats) => {
  if (!overview.exists || !overview.items) {
    return { activeGoals: 0, completedThisWeek: 0, weekProgress: 0, streak: 0 }
  }

  const activeGoals = userStats?.goals_count || overview.items.length
  const streak = userStats?.streak || 0
  const completedThisWeek = userStats?.recent_completions || 0
  const completionRate = userStats?.completion_rate || 0
  
  return {
    activeGoals,
    completedThisWeek,
    weekProgress: completionRate,
    streak
  }
}

export default Dashboard